import React from 'react';
import { 
  TrendingUp, 
  TrendingDown, 
  AlertTriangle, 
  CheckCircle, 
  Info,
  Target,
  BarChart3,
  PieChart,
  Award,
  Star,
  AlertCircle
} from 'lucide-react';
import SensitiveValue from './SensitiveValue';
import MiniChart from './MiniChart';

interface ProductPerformance {
  product_id: number;
  product_name: string;
  area_name: string;
  currency_code: string;
  currency_symbol: string;
  variation_percent: number;
  variation_direction: 'up' | 'down' | 'stable';
  current_value_eur: number;
  invested_value_eur: number;
  absolute_gain_loss_eur: number;
}

interface PortfolioInsightsProps {
  distributionData: {
    total_value_eur: number;
    by_area: Array<{
      area_id: number;
      area_name: string;
      target_percentage: number;
      current_value_eur: number;
      current_percentage: number;
      deviation_percentage: number;
      products: Array<{
        product_id: number;
        product_name: string;
        target_percentage: number;
        current_value_eur: number;
        current_percentage: number;
        deviation_percentage: number;
        currency_code: string;
      }>;
    }>;
    by_product: Array<{
      product_id: number;
      product_name: string;
      area_name: string;
      target_percentage: number;
      current_value_eur: number;
      current_percentage: number;
      currency_code: string;
    }>;
  };
  performanceData: ProductPerformance[];
  productEvolutionData?: Array<{
    product_id: number;
    product_name: string;
    area_name: string;
    evolution: Array<{
      date: string;
      value_eur: number;
      variation_percent: number;
    }>;
  }>;
}

const PortfolioInsights: React.FC<PortfolioInsightsProps> = ({ distributionData, performanceData, productEvolutionData = [] }) => {
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('pt-PT', {
      style: 'currency',
      currency: 'EUR',
    }).format(amount);
  };

  const formatPercentage = (value: number) => {
    return `${value >= 0 ? '+' : ''}${value.toFixed(1)}%`;
  };

  // Calcular insights
  const totalValue = distributionData.total_value_eur;
  const totalAreas = distributionData.by_area.length;
  const totalProducts = distributionData.by_product.length;
  
  // Área com maior desvio
  const areaWithMaxDeviation = distributionData.by_area.reduce((max, area) => 
    Math.abs(area.deviation_percentage) > Math.abs(max.deviation_percentage) ? area : max
  );
  
  // Produto com maior desvio (calculado dentro da área)
  const productWithMaxDeviation = distributionData.by_product.reduce((max, product) => {
    const area = distributionData.by_area.find(a => a.area_name === product.area_name);
    if (!area) return max;
    
    const areaTotalValue = area.products.reduce((sum, p) => sum + p.current_value_eur, 0);
    const areaTotalTarget = area.products.reduce((sum, p) => sum + p.target_percentage, 0);
    
    const productAreaPercentage = (product.current_value_eur / areaTotalValue) * 100;
    const productAreaTarget = (product.target_percentage / areaTotalTarget) * 100;
    const productDeviation = productAreaPercentage - productAreaTarget;
    
    const maxArea = distributionData.by_area.find(a => a.area_name === max.area_name);
    if (!maxArea) return product;
    
    const maxAreaTotalValue = maxArea.products.reduce((sum, p) => sum + p.current_value_eur, 0);
    const maxAreaTotalTarget = maxArea.products.reduce((sum, p) => sum + p.target_percentage, 0);
    
    const maxProductAreaPercentage = (max.current_value_eur / maxAreaTotalValue) * 100;
    const maxProductAreaTarget = (max.target_percentage / maxAreaTotalTarget) * 100;
    const maxProductDeviation = maxProductAreaPercentage - maxProductAreaTarget;
    
    return Math.abs(productDeviation) > Math.abs(maxProductDeviation) ? product : max;
  });
  
  // Área mais concentrada
  const mostConcentratedArea = distributionData.by_area.reduce((max, area) => 
    area.current_percentage > max.current_percentage ? area : max
  );

  // Melhor e pior performance
  const bestPerformer = performanceData.length > 0 ? performanceData[0] : null;
  const worstPerformer = performanceData.length > 0 ? performanceData[performanceData.length - 1] : null;
  
  // Produtos fora do alvo (calculado dentro da área)
  const productsOffTarget = distributionData.by_product.filter(product => {
    const area = distributionData.by_area.find(a => a.area_name === product.area_name);
    if (!area) return false;
    
    const areaTotalValue = area.products.reduce((sum, p) => sum + p.current_value_eur, 0);
    const areaTotalTarget = area.products.reduce((sum, p) => sum + p.target_percentage, 0);
    
    const productAreaPercentage = (product.current_value_eur / areaTotalValue) * 100;
    const productAreaTarget = (product.target_percentage / areaTotalTarget) * 100;
    const productDeviation = Math.abs(productAreaPercentage - productAreaTarget);
    
    return productDeviation > 5;
  });
  
  // Áreas fora do alvo
  const areasOffTarget = distributionData.by_area.filter(area => 
    Math.abs(area.deviation_percentage) > 5
  );

  const getDeviationColor = (deviation: number) => {
    if (Math.abs(deviation) < 2) return 'text-success-600 bg-success-50';
    if (Math.abs(deviation) < 5) return 'text-warning-600 bg-warning-50';
    return 'text-danger-600 bg-danger-50';
  };

  const getDeviationIcon = (deviation: number) => {
    if (Math.abs(deviation) < 2) return <CheckCircle className="h-4 w-4" />;
    if (Math.abs(deviation) < 5) return <AlertTriangle className="h-4 w-4" />;
    return <AlertTriangle className="h-4 w-4" />;
  };

  // Função para calcular risco baseado na volatilidade e oscilações
  const calculateRisk = (evolutionData: Array<{variation_percent: number}>) => {
    if (evolutionData.length < 2) return 1;
    
    const variations = evolutionData.map(d => d.variation_percent);
    
    // Calcular volatilidade (desvio padrão)
    const mean = variations.reduce((sum, val) => sum + val, 0) / variations.length;
    const variance = variations.reduce((sum, val) => sum + Math.pow(val - mean, 2), 0) / variations.length;
    const volatility = Math.sqrt(variance);
    
    // Calcular número de mudanças de direção (oscilações)
    let directionChanges = 0;
    for (let i = 1; i < variations.length; i++) {
      const prevChange = variations[i-1] - (i > 1 ? variations[i-2] : 0);
      const currentChange = variations[i] - variations[i-1];
      if ((prevChange > 0 && currentChange < 0) || (prevChange < 0 && currentChange > 0)) {
        directionChanges++;
      }
    }
    
    // Calcular amplitude (diferença entre máximo e mínimo)
    const amplitude = Math.max(...variations) - Math.min(...variations);
    
    // Combinar fatores: volatilidade, oscilações e amplitude
    const oscillationFactor = directionChanges / Math.max(1, variations.length - 1);
    const amplitudeFactor = amplitude / 10; // Normalizar amplitude
    
    const combinedRisk = (volatility * 0.4) + (oscillationFactor * 3) + (amplitudeFactor * 0.6);
    
    // Normalizar para escala 1-5
    if (combinedRisk < 0.5) return 1;
    if (combinedRisk < 1.0) return 2;
    if (combinedRisk < 1.5) return 3;
    if (combinedRisk < 2.0) return 4;
    return 5;
  };

  // Função para encontrar picos
  const findPeaks = (evolutionData: Array<{variation_percent: number}>) => {
    if (evolutionData.length === 0) return { min: 0, max: 0 };
    
    const variations = evolutionData.map(d => d.variation_percent);
    return {
      min: Math.min(...variations),
      max: Math.max(...variations)
    };
  };

  return (
    <div className="space-y-6">
      {/* Mini Cards dos Produtos */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {performanceData.map((product, index) => {
          // Encontrar dados de evolução para este produto
          const productEvolution = productEvolutionData.find(p => p.product_id === product.product_id);
          const evolutionData = productEvolution ? productEvolution.evolution : [];
          
          const risk = calculateRisk(evolutionData);
          const peaks = findPeaks(evolutionData);
          
          // Determinar se é o melhor ou pior desempenho
          const isBestPerformer = index === 0; // Primeiro da lista ordenada
          const isWorstPerformer = index === performanceData.length - 1; // Último da lista ordenada
          
          return (
            <div key={product.product_id} className={`bg-white rounded-lg shadow-md p-4 border border-gray-200 relative ${isBestPerformer ? 'ring-2 ring-yellow-400' : isWorstPerformer ? 'ring-2 ring-red-400' : ''}`}>
              {/* Cabeçalho com nome do produto */}
              <div className="mb-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-sm font-semibold text-gray-800">{product.product_name}</h3>
                    <p className="text-xs text-gray-600">{product.area_name}</p>
                  </div>
                  {isBestPerformer && (
                    <div className="flex items-center">
                      <Star className="h-4 w-4 text-yellow-500 fill-yellow-500" />
                    </div>
                  )}
                  {isWorstPerformer && (
                    <div className="flex items-center">
                      <AlertCircle className="h-4 w-4 text-red-500" />
                    </div>
                  )}
                </div>
              </div>
              
              {/* Crescimento em destaque */}
              <div className="mb-4 text-center">
                <div className={`text-2xl font-bold ${product.variation_percent >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {product.variation_percent >= 0 ? '+' : ''}{product.variation_percent.toFixed(2)}%
                </div>
                <div className={`text-sm ${product.absolute_gain_loss_eur >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                  <SensitiveValue>
                    {product.absolute_gain_loss_eur >= 0 ? '+' : ''}{formatCurrency(product.absolute_gain_loss_eur)}
                  </SensitiveValue>
                </div>
              </div>
              
              {/* Gráfico minimalista e indicadores */}
              <div className="flex justify-between items-end">
                {/* Gráfico minimalista */}
                <div className="flex-1">
                  <MiniChart 
                    data={evolutionData.slice(-10).map(d => d.variation_percent)}
                    width={60}
                    height={20}
                    color="#E5E7EB"
                  />
                </div>
                
                {/* Picos e Risco sem labels */}
                <div className="text-right">
                  <div className="text-xs mb-1">
                    <span className="text-red-600">{peaks.min.toFixed(2)}%</span>
                    <span className="text-gray-400 mx-1">/</span>
                    <span className="text-green-600">{peaks.max.toFixed(2)}%</span>
                  </div>
                  <div className="flex justify-end">
                    {[1, 2, 3, 4, 5].map((level) => {
                      let colorClass = 'bg-gray-300';
                      if (level <= risk) {
                        if (level === 1) {
                          colorClass = 'bg-green-500';
                        } else if (level === 2) {
                          colorClass = 'bg-yellow-500';
                        } else if (level === 3) {
                          colorClass = 'bg-orange-500';
                        } else if (level === 4) {
                          colorClass = 'bg-red-500';
                        } else if (level === 5) {
                          colorClass = 'bg-red-600';
                        }
                      }
                      return (
                        <div
                          key={level}
                          className={`w-2 h-2 rounded-full mx-0.5 ${colorClass}`}
                        />
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>


      {/* Produtos que Precisam de Atenção */}
      {productsOffTarget.length > 0 && (
        <div className="card">
          <h4 className="text-md font-medium text-gray-900 mb-3">Produtos que Precisam de Atenção</h4>
          <div className="space-y-2">
            {productsOffTarget.slice(0, 3).map((product) => {
              const area = distributionData.by_area.find(a => a.area_name === product.area_name);
              const areaTotalValue = area?.products.reduce((sum, p) => sum + p.current_value_eur, 0) || 1;
              const areaTotalTarget = area?.products.reduce((sum, p) => sum + p.target_percentage, 0) || 1;
              
              const productAreaPercentage = (product.current_value_eur / areaTotalValue) * 100;
              const productAreaTarget = (product.target_percentage / areaTotalTarget) * 100;
              
              return (
                <div key={product.product_id} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                  <div>
                    <p className="font-medium text-sm">{product.product_name}</p>
                    <p className="text-xs text-gray-600">{product.area_name}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-medium">
                      {productAreaPercentage.toFixed(1)}% 
                      <span className="text-gray-500">/ {productAreaTarget.toFixed(1)}%</span>
                    </p>
                    <p className="text-xs text-gray-600">
                      <SensitiveValue>
                        {formatCurrency(product.current_value_eur)}
                      </SensitiveValue>
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Recomendações Simples */}
      <div className="card">
        <h4 className="text-md font-medium text-gray-900 mb-3">Recomendações</h4>
        <div className="space-y-2">
          {areasOffTarget.length > 0 && (
            <div className="flex items-start p-3 bg-warning-50 rounded-lg">
              <AlertTriangle className="h-4 w-4 text-warning-600 mt-0.5 mr-2" />
              <p className="text-sm text-warning-800">
                {areasOffTarget.length} área(s) estão fora do alvo. Considere rebalancear.
              </p>
            </div>
          )}
          
          {mostConcentratedArea.current_percentage > 60 && (
            <div className="flex items-start p-3 bg-danger-50 rounded-lg">
              <AlertTriangle className="h-4 w-4 text-danger-600 mt-0.5 mr-2" />
              <p className="text-sm text-danger-800">
                Alta concentração em {mostConcentratedArea.area_name} ({mostConcentratedArea.current_percentage.toFixed(1)}%). 
                Considere diversificar mais.
              </p>
            </div>
          )}
          
          {productsOffTarget.length === 0 && areasOffTarget.length === 0 && (
            <div className="flex items-start p-3 bg-success-50 rounded-lg">
              <CheckCircle className="h-4 w-4 text-success-600 mt-0.5 mr-2" />
              <p className="text-sm text-success-800">
                Excelente! O portefólio está bem alinhado com os alvos definidos.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PortfolioInsights;
